import React from 'react';
import { Package, ShieldCheck, Truck, FileText, Clock, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Technical = () => {
  const finishes = [
    { name: 'Polished', description: 'High-gloss mirror finish, ideal for interiors and countertops' },
    { name: 'Honed', description: 'Smooth matte finish, perfect for high-traffic areas' },
    { name: 'Leather', description: 'Textured finish with subtle sheen, excellent for modern aesthetics' },
    { name: 'Flamed', description: 'Rough textured surface, ideal for outdoor and slip-resistant applications' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-[#FAFAF8] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-[#111111] mb-4">Technical & Export Details</h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Comprehensive information about our quality standards, processing capabilities, and export procedures.
          </p>
        </div>
      </section>

      {/* Quality Control Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Quality Control Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FAFAF8] rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#111111]">Quarry Selection</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Careful selection of blocks at the quarry, ensuring color consistency and minimal defects before processing.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FAFAF8] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#111111]">Processing Quality</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Multi-stage inspection during cutting, polishing, and finishing to maintain uniform thickness and quality.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FAFAF8] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#111111]">Final Grading</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Comprehensive grading system ensures only export-quality materials are shipped to international markets.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Finishes */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Available Finishes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {finishes.map((finish, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2 text-[#111111]">{finish.name}</h3>
                  <p className="text-gray-600 text-sm">{finish.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging & Shipping */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Packaging & Shipping</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/30112371/pexels-photo-30112371.jpeg"
                alt="Packaging"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <Truck className="text-[#111111] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[#111111]">Secure Packaging</h3>
                  <p className="text-gray-600 text-sm">
                    Wooden crates with fumigation certificates, protective foam, and moisture-resistant wrapping ensure safe transit.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Package className="text-[#111111] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[#111111]">Container Loading</h3>
                  <p className="text-gray-600 text-sm">
                    Optimized loading patterns maximize space utilization while preventing damage during ocean freight.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <FileText className="text-[#111111] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[#111111]">Shipping Terms</h3>
                  <p className="text-gray-600 text-sm">
                    FOB (Free on Board) and CIF (Cost, Insurance, Freight) terms available. Complete export documentation provided.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Export Information</h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-[#111111] font-semibold hover:no-underline">
                What types of granite products do you offer?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We offer a wide range of Indian granites. Product availability may vary based on material selection, finish requirements, and project specifications.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-[#111111] font-semibold hover:no-underline">
                Who do you typically work with?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We primarily work with B2B customers such as importers, distributors, developers, architects, and contractors. Our offerings are structured to support commercial, residential, and large-scale project requirements.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-[#111111] font-semibold hover:no-underline">
                How can I start a discussion for a potential order?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                You can initiate a discussion by sharing your requirements through our inquiry form or contact channels. Our team will review the details and guide you through available options, timelines, and next steps.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-[#111111] font-semibold hover:no-underline">
                What quality standards do you follow?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We follow international grading standards with multi-stage quality control. Each shipment includes detailed grading reports, thickness measurements, and finish specifications. Third-party inspection can be arranged if required.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-[#111111] font-semibold hover:no-underline">
                What documentation is provided for export?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Complete export documentation including: Commercial Invoice, Packing List, Bill of Lading, Certificate of Origin, Fumigation Certificate (for wooden packaging), and Quality Inspection Report. Additional certificates can be arranged as per destination country requirements.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Technical;